import {
  Address,
  AMOUNT_ZERO,
  MarketAssetMeta,
  SiloDataAsset,
} from "@silo/types";
import { Effect } from "effect";
import { ContractsClient } from "../contracts-client";
import { getTokenLabels } from "../utils/get-token-labels";
import { getCurveLpMeta } from "@silo/config";

interface GetSiloDataAssetParams {
  marketAssetMeta: MarketAssetMeta;
  user: Address | undefined;
}

export function getSiloDataAsset({
  marketAssetMeta,
  user,
}: GetSiloDataAssetParams) {
  return Effect.gen(function* () {
    const {
      protocolKey,
      chainKey,
      marketAddress,
      tokenAddress,
      assetKey,
      rawSymbol,
      rawName,
      decimals,
      isNative,
      priceProvider,
      maxLtv,
      lt,
      collateralShareToken,
      protectedShareToken,
      debtShareToken,
    } = marketAssetMeta;

    const { isCurveLpAsset, curveLpTokenAddress } = getCurveLpMeta({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const effectiveTokenAddress = curveLpTokenAddress ?? tokenAddress;

    const [{ symbol, name }, marketAssetState, marketAssetUserState] =
      yield* Effect.all(
        [
          getTokenLabels({ chainKey, tokenAddress, rawSymbol, rawName }),
          ContractsClient.getMarketAssetState({
            marketAddress,
            tokenAddress,
            decimals,
            collateralShareToken,
            protectedShareToken,
            debtShareToken,
          }),
          user
            ? ContractsClient.getMarketAssetUserState({
                user,
                marketAddress,
                tokenAddress,
                effectiveTokenAddress,
                protectedShareToken,
              })
            : Effect.succeed(undefined),
        ],
        { concurrency: "unbounded" }
      );

    const siloDataAsset: SiloDataAsset = {
      assetKey,
      tokenAddress,
      rawSymbol,
      symbol,
      rawName,
      name,
      decimals,
      isNative,
      priceProvider,
      maxLtv,
      lt,

      priceNative: marketAssetState.priceNative,
      priceUsd: marketAssetState.priceUsd,

      liquidity: marketAssetState.liquidity,
      utilization: marketAssetState.utilization,

      collateralTotal: marketAssetState.collateralTotal,
      collateralBaseApr: marketAssetState.collateralBaseApr,
      collateralRewardsApr: marketAssetState.collateralRewardsApr,
      collateralEmissionPerSecond: marketAssetState.collateralEmissionPerSecond,

      protectedTotal: marketAssetState.protectedTotal,
      protectedRewardsApr: marketAssetState.protectedRewardsApr,
      protectedEmissionPerSecond: marketAssetState.protectedEmissionPerSecond,

      debtTotal: marketAssetState.debtTotal,
      debtBaseApr: marketAssetState.debtBaseApr,
      debtRewardsApr: marketAssetState.debtRewardsApr,
      debtEmissionPerSecond: marketAssetState.debtEmissionPerSecond,

      tokenBalance: marketAssetUserState?.tokenBalance ?? AMOUNT_ZERO,
      collateralBalance: marketAssetUserState?.collateralBalance ?? AMOUNT_ZERO,
      protectedBalance: marketAssetUserState?.protectedBalance ?? AMOUNT_ZERO,
      debtBalance: marketAssetUserState?.debtBalance ?? AMOUNT_ZERO,
      projectedDebtBalance:
        marketAssetUserState?.projectedDebtBalance ?? AMOUNT_ZERO,

      effectiveTokenAddress,
      isCurveLpAsset,
    };

    return siloDataAsset;
  });
}
